import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import {
  InvoiceService,
  type CreateInvoiceDto,
  type Invoice,
  type InvoiceStatus,
  type InvoiceType,
} from '../../services/invoice.service';
import {
  useInvoicesPage,
  useInvoiceSummary,
  useInvoice,
  useCreateInvoice,
  useUpdateInvoice,
  useUpdateInvoiceStatus,
  useDeleteInvoice,
  useGenerateEInvoice,
} from '../../hooks/useInvoices';
import InvoiceForm, { GST_TYPE_LABELS, INVOICE_TYPE_LABELS } from '../../components/invoices/InvoiceForm';
import ViewInvoiceModal from '../../components/invoices/ViewInvoiceModal';
import {
  INVOICE_STATUS_META,
  formatInvoiceDate,
  getDaysOverdue,
  getEffectiveStatus,
} from '../../components/invoices/invoiceDisplay';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
} from '../../components/ui/FormControls';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_FILTERS: InvoiceStatus[] = ['draft', 'issued', 'overdue', 'paid', 'cancelled'];

// What each status change is called, and what the person is told before confirming it
const STATUS_ACTIONS: Record<'issued' | 'paid' | 'cancelled', { title: string; confirm: string; done: string; text: string }> = {
  issued: {
    title: 'Issue Invoice',
    confirm: 'Issue',
    done: 'Invoice issued',
    text: 'will be issued to the customer. After that its items and amounts can no longer be changed.',
  },
  paid: {
    title: 'Mark Invoice as Paid',
    confirm: 'Mark Paid',
    done: 'Invoice marked as paid',
    text: 'will be recorded as paid in full. This cannot be undone.',
  },
  cancelled: {
    title: 'Cancel Invoice',
    confirm: 'Cancel Invoice',
    done: 'Invoice cancelled',
    text: 'will be cancelled. It stays in the list for the record, and this cannot be undone.',
  },
};

const InvoiceList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<InvoiceType | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ invoice: Invoice; status: 'issued' | 'paid' | 'cancelled' } | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: invoicePage, isLoading, isFetching, error } = useInvoicesPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    invoiceType: typeFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = useInvoiceSummary();
  // A draft is loaded with its items before the form opens on it
  const { data: editingInvoice, isLoading: isEditingLoading } = useInvoice(isFormOpen ? editingId : null);
  const createMutation = useCreateInvoice();
  const updateMutation = useUpdateInvoice();
  const statusMutation = useUpdateInvoiceStatus();
  const deleteMutation = useDeleteInvoice();
  const eInvoiceMutation = useGenerateEInvoice();

  const invoices = invoicePage?.data ?? [];
  const total = invoicePage?.total ?? 0;
  const totalPages = invoicePage?.totalPages ?? 1;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (invoicePage && page > 1 && page > invoicePage.totalPages) {
      setPage(Math.max(1, invoicePage.totalPages));
    }
  }, [invoicePage, page]);

  const openCreate = () => {
    setEditingId(null);
    setIsFormOpen(true);
  };

  const openEdit = (invoice: Invoice) => {
    setEditingId(invoice.id);
    setIsFormOpen(true);
  };

  const openView = (invoice: Invoice) => {
    setViewInvoice(invoice);
    setIsViewOpen(true);
  };

  const handleSubmit = async (data: CreateInvoiceDto) => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        toast.success('Draft invoice updated');
      } else {
        const created = await createMutation.mutateAsync(data);
        toast.success(`Invoice ${created.invoiceNumber} saved as a draft`);
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, editingId ? 'Could not update the invoice' : 'Could not create the invoice'));
      throw err;
    }
  };

  const confirmStatus = async () => {
    if (!statusTarget) return;
    try {
      await statusMutation.mutateAsync({ id: statusTarget.invoice.id, status: statusTarget.status });
      toast.success(STATUS_ACTIONS[statusTarget.status].done);
      setStatusTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the invoice status'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Draft invoice deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the invoice'));
    }
  };

  const handleDownloadPDF = async (invoice: Invoice) => {
    setDownloadingId(invoice.id);
    try {
      const blob = await InvoiceService.downloadInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Could not create the PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleGenerateEInvoice = async (invoice: Invoice) => {
    try {
      const result = await eInvoiceMutation.mutateAsync(invoice.id);
      if (result?.Success) toast.success('E-invoice generated');
      else toast.error(`E-invoice was not generated: ${result?.error?.message || 'unknown error'}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not generate the e-invoice'));
    }
  };

  const hasFilters = !!(search || statusFilter || typeFilter || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const openCount = (summary?.issued ?? 0) + (summary?.overdue ?? 0);
  const stats = [
    {
      label: 'Total Invoices',
      value: formatNumber(summary?.total ?? 0),
      hint: `${formatNumber(summary?.draft ?? 0)} drafts, ${formatNumber(summary?.cancelled ?? 0)} cancelled`,
      icon: DocumentTextIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Awaiting Payment',
      value: formatMoney(summary?.outstandingAmount ?? 0),
      hint: `${formatNumber(openCount)} issued ${openCount === 1 ? 'invoice' : 'invoices'}`,
      icon: ClockIcon,
      tone: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Overdue',
      value: formatMoney(summary?.overdueAmount ?? 0),
      hint: summary?.overdue
        ? `${formatNumber(summary.overdue)} past the due date`
        : 'Nothing is past its due date',
      icon: ExclamationTriangleIcon,
      tone: summary?.overdue ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-100',
    },
    {
      label: 'Paid',
      value: formatMoney(summary?.paidAmount ?? 0),
      hint: `${formatNumber(summary?.paid ?? 0)} ${summary?.paid === 1 ? 'invoice' : 'invoices'}`,
      icon: CheckCircleIcon,
      tone: 'text-green-600 bg-green-50',
    },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';
  const actionButtonClass = 'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold';

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading invoices</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
          <p className="mt-1 text-sm text-gray-600">Raise GST invoices, issue them and track what has been paid</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Invoice
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">{isSummaryLoading ? '—' : stat.value}</p>
              <p className="truncate text-xs text-gray-500">{isSummaryLoading ? ' ' : stat.hint}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice number, customer name or GST number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as InvoiceStatus | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {STATUS_FILTERS.map((status) => (
                <option key={status} value={status}>
                  {INVOICE_STATUS_META[status].label}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by invoice type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as InvoiceType | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All types</option>
              {(Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[]).map((type) => (
                <option key={type} value={type}>
                  {INVOICE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Invoice date
              <input
                type="date"
                aria-label="Invoice date from"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className={selectClass}
              />
              to
              <input
                type="date"
                aria-label="Invoice date to"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className={selectClass}
              />
            </label>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Invoices table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Invoice</th>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Invoice Date</th>
                <th className={thClass}>Due Date</th>
                <th className={thClass}>Tax</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={8}>
                    Loading invoices...
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No invoices found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Create an invoice to bill a customer.'}
                    </p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const status = getEffectiveStatus(invoice);
                  const isOpen = status === 'issued' || status === 'overdue';
                  const canEInvoice = isOpen && !invoice.eInvoiceIRN && !!invoice.customerGSTIN && invoice.grandTotal >= 50000;
                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div>
                          <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                            {invoice.invoiceNumber}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">{INVOICE_TYPE_LABELS[invoice.invoiceType]}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{invoice.customerName}</div>
                        <div className="font-mono text-xs text-gray-500">{invoice.customerGSTIN || 'Unregistered'}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatInvoiceDate(invoice.invoiceDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        <div>{formatInvoiceDate(invoice.dueDate)}</div>
                        {status === 'overdue' && (
                          <div className="text-xs text-red-600">{getDaysOverdue(invoice)} days overdue</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        <div>{GST_TYPE_LABELS[invoice.gstType]}</div>
                        <div className="text-xs text-gray-500">{invoice.placeOfSupply}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                        <div className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.grandTotal)}</div>
                        <div className="text-xs text-gray-500">
                          {invoice.itemCount ?? 0} {invoice.itemCount === 1 ? 'item' : 'items'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`${badgeClass} ${INVOICE_STATUS_META[status].color}`}>
                          {INVOICE_STATUS_META[status].label}
                        </span>
                        {invoice.eInvoiceIRN && (
                          <span className="ml-1 text-xs text-purple-700" title={`IRN ${invoice.eInvoiceIRN}`}>
                            e-invoice
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {status === 'draft' && (
                            <button
                              onClick={() => setStatusTarget({ invoice, status: 'issued' })}
                              className={`${actionButtonClass} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}
                            >
                              <PaperAirplaneIcon className="h-3.5 w-3.5 mr-1" />
                              Issue
                            </button>
                          )}
                          {isOpen && (
                            <button
                              onClick={() => setStatusTarget({ invoice, status: 'paid' })}
                              className={`${actionButtonClass} bg-green-50 text-green-700 hover:bg-green-100`}
                            >
                              <BanknotesIcon className="h-3.5 w-3.5 mr-1" />
                              Mark Paid
                            </button>
                          )}
                          <button
                            onClick={() => openView(invoice)}
                            className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                            title="View Invoice"
                            aria-label="View Invoice"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(invoice)}
                            disabled={downloadingId === invoice.id}
                            className={`${iconButtonClass} text-teal-600 hover:bg-teal-50 hover:text-teal-900`}
                            title="Download PDF"
                            aria-label="Download PDF"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                          {canEInvoice && (
                            <button
                              onClick={() => handleGenerateEInvoice(invoice)}
                              disabled={eInvoiceMutation.isPending}
                              className={`${iconButtonClass} text-purple-600 hover:bg-purple-50 hover:text-purple-900`}
                              title="Generate E-Invoice"
                              aria-label="Generate E-Invoice"
                            >
                              <BoltIcon className="h-4 w-4" />
                            </button>
                          )}
                          {status === 'draft' && (
                            <button
                              onClick={() => openEdit(invoice)}
                              className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                              title="Edit Draft"
                              aria-label="Edit Draft"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {(status === 'draft' || isOpen) && (
                            <button
                              onClick={() => setStatusTarget({ invoice, status: 'cancelled' })}
                              className={`${iconButtonClass} text-yellow-700 hover:bg-yellow-50 hover:text-yellow-900`}
                              title="Cancel Invoice"
                              aria-label="Cancel Invoice"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                          {status === 'draft' && (
                            <button
                              onClick={() => setDeleteTarget(invoice)}
                              className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                              title="Delete Draft"
                              aria-label="Delete Draft"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} invoices
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="lg:flex-1" currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Create, or edit a draft */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingId ? 'Edit Draft Invoice' : 'Create Invoice'}
        description={
          editingId
            ? editingInvoice
              ? `${editingInvoice.invoiceNumber} · ${editingInvoice.customerName}`
              : undefined
            : 'The invoice is saved as a draft. Issue it from the list once it is right.'
        }
        size="2xl"
        className="overflow-visible"
      >
        {editingId && (isEditingLoading || !editingInvoice || editingInvoice.id !== editingId) ? (
          <p className="py-12 text-center text-sm text-gray-500">Loading invoice...</p>
        ) : (
          <InvoiceForm
            key={editingId ? `${editingId}-${editingInvoice?.updatedAt}` : 'new'}
            invoice={editingId ? editingInvoice : null}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormOpen(false)}
            isSaving={isSaving}
          />
        )}
      </Modal>

      <ViewInvoiceModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        invoice={viewInvoice}
        onDownloadPDF={handleDownloadPDF}
        onEdit={(invoice) => {
          setIsViewOpen(false);
          openEdit(invoice);
        }}
        isDownloading={!!viewInvoice && downloadingId === viewInvoice.id}
      />

      {/* Issue, mark paid or cancel */}
      <ConfirmDialog
        isOpen={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        onConfirm={confirmStatus}
        title={statusTarget ? STATUS_ACTIONS[statusTarget.status].title : ''}
        message={
          statusTarget && (
            <>
              <span className="font-medium text-gray-900">{statusTarget.invoice.invoiceNumber}</span> for{' '}
              {statusTarget.invoice.customerName} ({formatMoney(statusTarget.invoice.grandTotal)}){' '}
              {STATUS_ACTIONS[statusTarget.status].text}
            </>
          )
        }
        confirmLabel={statusTarget ? STATUS_ACTIONS[statusTarget.status].confirm : ''}
        pendingLabel="Updating..."
        isPending={statusMutation.isPending}
        tone={statusTarget?.status === 'cancelled' ? 'danger' : 'primary'}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Draft Invoice"
        message={
          <>
            Draft <span className="font-medium text-gray-900">{deleteTarget?.invoiceNumber}</span> for{' '}
            {deleteTarget?.customerName} will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default InvoiceList;
